import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { requestCommitmentStatuses } from 'legacy/lut/features/partner360/commitment'
import { requestRecalculateCompleteness } from 'legacy/lut/features/partner360/completeness'
import { useSignature } from 'legacy/lut/hooks/useSignature'
import { FC, useCallback, useEffect } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Checkbox } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'

import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { HintComponent } from '../../../HintComponent'

import { SIGNATURE_DROP_DIALOG, SignatureDropDialog } from './components/signatureDropDialog'
import { useBpsCategoryFlag } from './hooks/useBpsCategoryFlag'
import { useContractualPlanning } from './hooks/useContractualPlanning'
import { usePowerServicesMembershipParticipant } from './hooks/usePowerServicesMembershipParticipant'
import { IPowerServicesMembershipProps } from './models/powerServicesMembershipProps'

import styles from './PowerServicesMembership.module.scss'

export const PowerServicesMembership: FC<IPowerServicesMembershipProps> = ({
  partnerId,
  programType,
  fYear,
  metrics,

  onUpdateSectionSettings = () => {},
}) => {
  /** -------------------- Variables ----------------------- */
  const isCorePrint = programType === ProgramTypes.CorePrint
  const isPersonalSystems = programType === ProgramTypes.PersonalSystems

  /** -------------------- hooks ----------------------- */
  const dispatch = useAppDispatch()
  const { confirm } = useConfirm()
  const { isPowerServicesParticipant, setPowerServicesParticipant, isLoading } =
    usePowerServicesMembershipParticipant()
  const { isContractualPlanningActive, setContractualPlanningActive } = useContractualPlanning({
    programType,
  })
  const { isBpsCategoryApplicable, setBpsCategoryApplicable } = useBpsCategoryFlag()
  const { signatureId, dropSignature } = useSignature()

  /** -------------------- Methods ----------------------- */
  const getConfirmationFromUserToDropSignature = useCallback(async (): Promise<
    'drop' | 'keep' | 'unsigned'
  > => {
    if (signatureId !== undefined) {
      const shouldDropSignature = await confirm(SIGNATURE_DROP_DIALOG)
      return shouldDropSignature ? 'drop' : 'keep'
    }

    return 'unsigned'
  }, [signatureId])

  const dropSignatureAndTriggerCompleteness = useCallback(async () => {
    onUpdateSectionSettings(false)
    await dropSignature('Financial & SOB Targets')

    const parameters = {
      partnerId,
      fYear,
    }

    await Promise.all([
      dispatch(requestRecalculateCompleteness(parameters)),
      dispatch(requestCommitmentStatuses(parameters)),
    ])
    onUpdateSectionSettings(true)
  }, [dispatch, dropSignature, onUpdateSectionSettings, partnerId, fYear])

  const handleCorePrintCase = useCallback(
    async (newState: boolean) => {
      if (newState && !isContractualPlanningActive) {
        const userDecision = await getConfirmationFromUserToDropSignature()
        if (userDecision === 'keep') {
          return
        }

        if (userDecision === 'drop') {
          await dropSignatureAndTriggerCompleteness()
        }
      }

      await setPowerServicesParticipant(newState)
      if (!isContractualPlanningActive) {
        onUpdateSectionSettings(false)
        await setContractualPlanningActive()
        onUpdateSectionSettings(true)
      }
    },
    [
      getConfirmationFromUserToDropSignature,
      isContractualPlanningActive,
      setContractualPlanningActive,
      setPowerServicesParticipant,
      dropSignatureAndTriggerCompleteness,
      onUpdateSectionSettings,
    ]
  )

  const handlePersonalSystemsCase = useCallback(
    async (newState: boolean) => {
      if (newState && !isBpsCategoryApplicable) {
        const userDecision = await getConfirmationFromUserToDropSignature()
        if (userDecision === 'keep') {
          return
        }

        if (userDecision === 'drop') {
          await dropSignatureAndTriggerCompleteness()
        }
      }

      await setPowerServicesParticipant(newState)
      if (!isBpsCategoryApplicable) {
        onUpdateSectionSettings(false)
        await setBpsCategoryApplicable()
        onUpdateSectionSettings(true)
      }
    },
    [
      isBpsCategoryApplicable,
      setBpsCategoryApplicable,
      onUpdateSectionSettings,
      setPowerServicesParticipant,
      dropSignatureAndTriggerCompleteness,
      getConfirmationFromUserToDropSignature,
    ]
  )

  const handleChange = useCallback(
    async (newState: boolean) => {
      if (isCorePrint) {
        await handleCorePrintCase(newState)
      }

      if (isPersonalSystems) {
        await handlePersonalSystemsCase(newState)
      }
    },
    [isCorePrint, isPersonalSystems, handleCorePrintCase, handlePersonalSystemsCase]
  )

  useEffect(() => {
    if (!isPersonalSystems) {
      return
    }

    const isBpsNotApplicable = !isBpsCategoryApplicable
    if (isPowerServicesParticipant && isBpsNotApplicable) {
      handleChange(true)
    }
  }, [isPowerServicesParticipant, isPersonalSystems, isBpsCategoryApplicable, handleChange])

  return (
    <div className={styles.root}>
      <Checkbox
        disabled={isLoading}
        checked={isPowerServicesParticipant}
        onChange={handleChange}
        label={<Translation text="jbp-planning-power-services-membership-helper" />}
      />
      <HintComponent
        text={
          isCorePrint
            ? 'jbp-planning-power-services-membership-helper-hint-for-core-print'
            : 'jbp-planning-power-services-membership-helper-hint-for-personal-systems'
        }
      />
      {isLoading && <Loader className={styles.loader} dimension={{ width: 24, height: 24 }} />}
      <SignatureDropDialog
        metrics={metrics}
        partnerId={partnerId}
        fYear={fYear}
        programType={programType}
      />
    </div>
  )
}
